import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Preferred Employer Program — participating partner list.
 *
 * A single richtext bullet list so authors can add, remove, or reorder
 * partners freely with the editor's list controls (no fixed slot count).
 */
export function PreferredEmployerList() {
  useRevealStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const listRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pep-list">
      <div className="pep-list-container">
        <div ref={headerRef} className="reveal-fade-up pep-list-header">
          <RichTextContent
            path="preferredemployerlist.heading"
            as="h2"
            className="pep-list-heading"
          />
          <div className="pep-list-divider" aria-hidden="true" />
        </div>

        <div ref={listRef} className="reveal-fade-up pep-list-body">
          <RichTextContent
            path="preferredemployerlist.employers"
            as="div"
            className="pep-employers"
          />
        </div>

        <div className="pep-list-footnote">
          <RichTextContent
            path="preferredemployerlist.footnote"
            as="p"
            className="pep-list-footnote-text"
          />
        </div>
      </div>

      <style>{`
        .pep-list {
          padding: 2.5rem 0 3.5rem;
          background: linear-gradient(180deg,
            var(--vibe-color-background, #ffffff) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 5%, var(--vibe-color-background, #ffffff)) 100%
          );
        }
        @media (min-width: 768px) {
          .pep-list { padding: 4rem 0 5rem; }
        }

        .pep-list-container {
          max-width: 44rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) {
          .pep-list-container { padding: 0 1.5rem; }
        }

        .pep-list-header {
          text-align: center;
          margin: 0 auto 2rem;
        }

        .pep-list-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.2vw, 2rem);
          font-weight: 400;
          color: var(--vibe-color-text);
          letter-spacing: 0.01em;
          margin: 0 0 1rem;
        }
        .pep-list-heading p { margin: 0; }

        .pep-list-divider {
          width: 48px;
          height: 2px;
          margin: 0 auto;
          background: var(--vibe-color-primary);
          opacity: 0.85;
        }

        .pep-list-body {
          background: #fff;
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          border-radius: var(--vibe-radius-md, 6px);
          padding: 1.5rem 1.75rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.03);
        }
        @media (min-width: 640px) {
          .pep-list-body { padding: 1.75rem 2.25rem; }
        }

        .pep-employers {
          font-family: var(--vibe-font-body);
          font-size: 0.98rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
        }
        .pep-employers ul {
          list-style: none;
          padding: 0;
          margin: 0;
          columns: 1;
        }
        @media (min-width: 640px) {
          .pep-employers ul { columns: 2; column-gap: 2rem; }
        }
        .pep-employers li {
          position: relative;
          padding: 0.4rem 0 0.4rem 1.4rem;
          break-inside: avoid;
        }
        .pep-employers li::before {
          content: '';
          position: absolute;
          left: 0.35rem;
          top: 0.95rem;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }
        .pep-employers p { margin: 0 0 0.75rem; }
        .pep-employers p:last-child { margin-bottom: 0; }
        .pep-employers a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .pep-list-footnote {
          text-align: center;
        }

        .pep-list-footnote-text {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text-muted);
          font-style: italic;
          margin: 0;
        }
        .pep-list-footnote-text p { margin: 0; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerList',
  displayName: 'Preferred Employer — Partner List',
  tokens: {
    'preferredemployerlist.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Our Preferred Employer Partners</p>',
    },
    'preferredemployerlist.employers': {
      label: 'Partner List',
      type: 'richtext',
      description:
        'A bullet list of participating employers. Add or remove items using the editor\'s list controls.',
      defaultValue:
        '<ul>' +
        '<li>Local Hospitals &amp; Healthcare Systems</li>' +
        '<li>Public School Districts</li>' +
        '<li>Colleges &amp; Universities</li>' +
        '<li>Police &amp; Fire Departments</li>' +
        '<li>City &amp; County Government</li>' +
        '<li>Federal Agencies</li>' +
        '<li>Fortune 500 Employers</li>' +
        '<li>Nonprofit &amp; Community Organizations</li>' +
        '</ul>',
    },
    'preferredemployerlist.footnote': {
      label: 'Footnote',
      type: 'richtext',
      defaultValue:
        '<p>Don\'t see your employer? Contact our leasing team — we\'re always growing our partner list.</p>',
    },
  },
};

(PreferredEmployerList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerList;
